import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Button from '../../components/ui/Button';
import Icon from '../../components/AppIcon';
import workflowSaveService from '../../services/workflowSaveService';
import projectService from '../../services/projectService';
import Breadcrumb from '../module-landing-page/components/Breadcrumb';

const WorkflowViewOnlyDisplay = () => {
  const { projectId, moduleId } = useParams();
  const navigate = useNavigate();
  const [workflow, setWorkflow] = useState(null);
  const [project, setProject] = useState(null);
  const [module, setModule] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [zoomLevel, setZoomLevel] = useState(100);
  const [panPosition, setPanPosition] = useState({ x: 0, y: 0 });
  const [isPanning, setIsPanning] = useState(false);
  const [panStart, setPanStart] = useState({ x: 0, y: 0 });
  const [imageError, setImageError] = useState(false);

  const getConnectionPath = (sourceNode, targetNode) => {
    const startX = sourceNode?.position?.x + 100;
    const startY = sourceNode?.position?.y + 40;
    const endX = targetNode?.position?.x;
    const endY = targetNode?.position?.y + 40;

    const midX = (startX + endX) / 2;
    return `M ${startX} ${startY} C ${midX} ${startY}, ${midX} ${endY}, ${endX} ${endY}`;
  };

  useEffect(() => {
    if (projectId && moduleId) {
      loadWorkflowData();
    }
  }, [projectId, moduleId]);

  const loadWorkflowData = async () => {
    setLoading(true);
    setError(null);

    try {
      // Load project data
      const projectData = await projectService?.getProjectById(projectId);
      setProject(projectData);

      // Load module data
      const moduleResult = await workflowSaveService?.getModuleById(moduleId);
      if (moduleResult?.error) throw new Error(moduleResult?.error);
      setModule(moduleResult?.data);

      // Load workflow data - now filtered by module
      const workflowResult = await workflowSaveService?.loadWorkflowFromModule(moduleId);
      if (workflowResult?.error) throw new Error(workflowResult?.error);
      
      if (!workflowResult?.data) {
        // No workflow exists for this specific module, redirect to designer
        navigate(`/project/${projectId}/module/${moduleId}/workflow`);
        return;
      }

      // Verify workflow belongs to this module
      if (workflowResult?.data?.moduleId !== moduleId) {
        throw new Error('Workflow data mismatch - this workflow belongs to a different module');
      }

      setWorkflow(workflowResult?.data);
      console.log('Loaded workflow for module:', moduleId, 'Project:', projectId);
    } catch (err) {
      setError(err?.message || 'Failed to load workflow data');
      console.error('Error loading workflow:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleEditWorkflow = () => {
    navigate(`/project/${projectId}/module/${moduleId}/workflow`);
  };

  const handleBackToModule = () => {
    navigate(`/project/${projectId}/module/${moduleId}`);
  };

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 10, 200));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - 10, 50));
  };

  const handleResetView = () => {
    setZoomLevel(100);
    setPanPosition({ x: 0, y: 0 });
  };

  const handleMouseDown = (e) => {
    if (e?.button === 0) {
      setIsPanning(true);
      setPanStart({ x: e?.clientX - panPosition?.x, y: e?.clientY - panPosition?.y });
    }
  };

  const handleMouseMove = (e) => {
    if (isPanning) {
      setPanPosition({
        x: e?.clientX - panStart?.x,
        y: e?.clientY - panStart?.y
      });
    }
  };

  const handleMouseUp = () => {
    setIsPanning(false);
  };

  const getNodeStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'var(--color-success)';
      case 'in-progress':
        return 'var(--color-warning)';
      case 'pending':
      default:
        return 'var(--color-muted-foreground)';
    }
  };

  if (loading) {
    return (
      <div className="flex h-screen bg-[var(--color-background)]">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center ml-60">
          <div className="text-[var(--color-muted-foreground)]">
            <Icon name="loader" className="animate-spin" size={32} />
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen bg-[var(--color-background)]">
        <Sidebar />
        <div className="flex-1 flex items-center justify-center ml-60">
          <div className="text-center">
            <p className="text-[var(--color-destructive)] mb-4">{error}</p>
            <Button onClick={handleBackToModule}>Back to Module</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-[var(--color-background)]">
      <Sidebar />
      <div className="flex-1 flex flex-col overflow-hidden transition-all duration-250" style={{ marginLeft: projectId ? 'calc(var(--sidebar-width, 240px) + var(--project-submenu-width, 192px))' : 'var(--sidebar-width, 240px)' }}>
        {/* Header */}
        <div className="border-b border-[var(--color-border)] bg-[var(--color-card)]">
          <div className="px-6 py-2">
            <Breadcrumb
              projectId={projectId}
              projectName={project?.data?.name}
              moduleId={moduleId}
              moduleName={module?.name}
              workflowName={workflow?.name || 'Workflow View'}
              workflowLevel="View"
            />
          </div>
          
          {/* Workflow Header with Project Logo */}
          <div className="px-6 py-3 flex items-center justify-between gap-4 border-t border-[var(--color-border)]">
            <div className="flex items-center gap-4 flex-1">
              {/* Project Logo/Image */}
              <div className="w-12 h-12 rounded-lg overflow-hidden bg-[var(--color-muted)] flex items-center justify-center flex-shrink-0">
                {project?.data?.image_url && !imageError ? (
                  <img
                    src={project?.data?.image_url}
                    alt={project?.data?.name || 'Project'}
                    className="w-full h-full object-cover"
                    onError={() => setImageError(true)}
                  />
                ) : (
                  <Icon name="folder" size={24} className="text-[var(--color-muted-foreground)]" />
                )}
              </div>

              {/* Workflow Info */}
              <div>
                <h1 className="text-2xl font-bold text-[var(--color-foreground)]">
                  {workflow?.name || 'Workflow View'}
                </h1>
                <p className="text-xs text-[var(--color-muted-foreground)]">
                  {workflow?.nodes?.length || 0} nodes • Read-only mode
                </p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="default"
                size="sm"
                onClick={handleEditWorkflow}
                className="shadow-md hover:shadow-lg transition-all duration-300"
              >
                <Icon name="Edit" size={16} />
                <span className="font-medium">Edit Workflow</span>
              </Button>
            </div>
          </div>
        </div>

        {/* Main Canvas Area */}
        <div className="h-[75%] relative bg-muted/10 overflow-hidden">
          {/* Zoom Controls */}
          <div className="absolute top-4 right-4 z-10 flex flex-col gap-2 bg-card border border-border rounded-lg p-2 shadow-lg">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomIn}
              className="w-8 h-8 p-0"
            >
              <Icon name="ZoomIn" size={16} />
            </Button>
            <div className="text-xs text-center font-medium text-foreground">{zoomLevel}%</div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleZoomOut}
              className="w-8 h-8 p-0"
            >
              <Icon name="ZoomOut" size={16} />
            </Button>
            <div className="h-px bg-border my-1" />
            <Button
              variant="ghost"
              size="sm"
              onClick={handleResetView}
              className="w-8 h-8 p-0"
            >
              <Icon name="Maximize2" size={16} />
            </Button>
          </div>

          {/* Canvas */}
          <div
            className="w-full h-full cursor-grab active:cursor-grabbing"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
          >
            <div
              style={{
                transform: `translate(${panPosition?.x}px, ${panPosition?.y}px) scale(${zoomLevel / 100})`,
                transformOrigin: 'center center',
                transition: isPanning ? 'none' : 'transform 0.2s ease-out',
                width: '100%',
                height: '100%',
                position: 'relative'
              }}
            >
              {/* Grid Background */}
              <div
                className="absolute inset-0"
                style={{
                  backgroundImage: 'radial-gradient(circle, var(--color-muted) 1px, transparent 1px)',
                  backgroundSize: `${20 * (zoomLevel / 100)}px ${20 * (zoomLevel / 100)}px`,
                  backgroundPosition: `${panPosition?.x}px ${panPosition?.y}px`
                }}
              />

              {/* Render Connections */}
              <svg
                className="absolute inset-0"
                style={{
                  width: '100%',
                  height: '100%',
                  pointerEvents: 'none'
                }}
              >
                {workflow?.connections?.map((conn, index) => {
                  const sourceNode = workflow?.nodes?.find(n => n?.id === conn?.source);
                  const targetNode = workflow?.nodes?.find(n => n?.id === conn?.target);
                  if (!sourceNode || !targetNode) return null;

                  return (
                    <g key={index}>
                      {/* Visible connection path */}
                      <path
                        d={getConnectionPath(sourceNode, targetNode)}
                        stroke="var(--color-primary)"
                        strokeWidth="2"
                        fill="none"
                        transform={`translate(${panPosition?.x}, ${panPosition?.y}) scale(${zoomLevel / 100})`}
                        style={{ pointerEvents: 'none' }}
                      />
                      <circle
                        cx={targetNode?.position?.x}
                        cy={targetNode?.position?.y + 40}
                        r="4"
                        fill="var(--color-primary)"
                        transform={`translate(${panPosition?.x}, ${panPosition?.y}) scale(${zoomLevel / 100})`}
                      />
                    </g>
                  );
                })}
              </svg>

              {/* Render Nodes */}
              <div
                className="absolute"
                style={{
                  transform: `translate(${panPosition?.x}px, ${panPosition?.y}px) scale(${zoomLevel / 100})`,
                  transformOrigin: '0 0'
                }}
              >
                {workflow?.nodes?.map((node) => (
                  <div
                    key={node?.id}
                    className="absolute bg-card rounded-xl shadow-lg border-2 border-border transition-all duration-250"
                    style={{
                      left: `${node?.position?.x || 0}px`,
                      top: `${node?.position?.y || 0}px`,
                      width: '200px',
                      minHeight: '80px'
                    }}
                  >
                    <div className="flex items-center gap-3 px-4 py-3 border-b border-border">
                      <div
                        className="flex items-center justify-center w-10 h-10 rounded-lg flex-shrink-0"
                        style={{ backgroundColor: `${node?.color || 'var(--color-primary)'}20`, color: node?.color || 'var(--color-primary)' }}
                      >
                        <Icon name={node?.icon || 'Box'} size={20} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <h4 className="text-sm font-medium text-foreground truncate">{node?.name || node?.label || 'Untitled Node'}</h4>
                        <p className="text-xs text-muted-foreground">{node?.type || 'Task'}</p>
                      </div>
                    </div>
                    <div className="px-4 py-3">
                      <div className="flex items-center justify-between text-xs">
                        <span className="text-muted-foreground">Duration:</span>
                        <span className="text-foreground font-medium">{node?.data?.duration || 0}h</span>
                      </div>
                      <div className="flex items-center justify-between text-xs mt-2">
                        <span className="text-muted-foreground">Workers:</span>
                        <span className="text-foreground font-medium">{node?.data?.workers || 0}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Status Panel */}
        <div className="h-[13%] bg-card border-t border-border px-4 py-3">
          <div className="flex items-center justify-between h-full">
            <div className="flex items-center gap-6">
              <div>
                <p className="text-xs text-muted-foreground mb-1">Last Modified</p>
                <p className="text-sm font-medium text-foreground">
                  {workflow?.metadata?.savedAt ? new Date(workflow?.metadata?.savedAt)?.toLocaleString() : 'N/A'}
                </p>
              </div>
              <div className="h-8 w-px bg-border" />
              <div>
                <p className="text-xs text-muted-foreground mb-1">Workflow Status</p>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full bg-success" />
                  <p className="text-sm font-medium text-foreground">Active</p>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 rounded-lg">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--color-success)' }} />
                <span className="text-xs text-muted-foreground">Completed</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 rounded-lg">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--color-warning)' }} />
                <span className="text-xs text-muted-foreground">In Progress</span>
              </div>
              <div className="flex items-center gap-2 px-3 py-2 bg-muted/30 rounded-lg">
                <div className="w-3 h-3 rounded-full" style={{ backgroundColor: 'var(--color-muted-foreground)' }} />
                <span className="text-xs text-muted-foreground">Pending</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkflowViewOnlyDisplay;